import React, { useRef, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  ScrollErrorForm,
  ConfirmationCheckbox,
  ConfirmModal,
  Notification,
  Spinner,
  PromoCode,
} from 'components'
import { Formik } from 'formik'
import * as Yup from 'yup'
import * as Types from 'types'
import { displayAttribute, formatCurrency } from 'utils'
import { isEmpty } from 'lodash'
import PaymentFields, { paymentSchema } from 'components/PaymentFields'
import { PAYMENT_CONFIRMATION_TEXT } from '../../../config/benefit-distributions'
import { Variant as NotificationVariant } from 'components/Notification'
import { ParticipantsSummaryTable, FlowActions } from '../components'
import { isPromoCodeEnabled } from 'config/portal'

const propTypes = {
  initialValues: PropTypes.object.isRequired,
  hasBalance: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsNewPayment: PropTypes.func.isRequired,
  showReplacePayment: PropTypes.bool.isRequired,
  participantsSummaryData: PropTypes.array.isRequired,
  isCreditCardPayment: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  currentUser: Types.user.isRequired,
  paymentMethod: PropTypes.array,
  onConfirmDeletePayment: PropTypes.func.isRequired,
  totalPaymentDue: PropTypes.number,
  hasCardOnFile: PropTypes.bool.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  applyPromoCode: PropTypes.func.isRequired,
}

const defaultProps = {
  totalPaymentDue: null,
}

function ReviewAndSubmitForm({
  onSubmit,
  isLoading,
  initialValues,
  setIsNewPayment,
  showReplacePayment,
  participantsSummaryData,
  isCreditCardPayment,
  currentUser,
  paymentMethod,
  onConfirmDeletePayment,
  hasBalance,
  totalPaymentDue,
  hasCardOnFile,
  flashSuccessMessageHandler,
  applyPromoCode,
  participantsForSubmission,
}) {
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
  const [showRemovePaymentModal, setShowRemovePaymentModal] = useState(false)
  const hasPaymentMethod = !isEmpty(paymentMethod)
  const baseTotalRef = useRef(null)

  const getPaymentDetailsSchema = (discountedTotal) => {
    if (discountedTotal <= 0) return Yup.mixed().nullable()
    if (!isCreditCardPayment) return Yup.object().nullable()
    if (hasBalance && !hasPaymentMethod) return paymentSchema
    if (!hasBalance) return Yup.object().nullable()
    if (hasPaymentMethod && paymentMethod.creditCardLastFour)
      return Yup.object().nullable()
    if (hasPaymentMethod && hasBalance) return paymentSchema

    return Yup.object().nullable()
  }

  const getConfirmPaymentSchema = (discountedTotal) => {
    if (discountedTotal <= 0) return Yup.boolean().nullable()
    if (isCreditCardPayment && hasBalance)
      return Yup.boolean().required('Required').oneOf([true], 'Required')
    return Yup.boolean().nullable()
  }

  const confirmSubmissionSchema = useMemo(
    () =>
      Yup.object({
        paymentDetails: Yup.lazy((values, options) =>
          getPaymentDetailsSchema(
            options.context.discountedTotal ?? totalPaymentDue ?? 0
          )
        ),
        confirmPayment: Yup.lazy((values, options) =>
          getConfirmPaymentSchema(
            options.context.discountedTotal ?? totalPaymentDue ?? 0
          )
        ),
      }),
    [
      hasPaymentMethod,
      paymentMethod,
      isCreditCardPayment,
      hasBalance,
      totalPaymentDue,
    ]
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={confirmSubmissionSchema}
    >
      {({
        touched,
        errors,
        isSubmitting,
        values,
        setFieldValue,
        setFieldError,
      }) => {
        if (!values.promoApplied && baseTotalRef.current === null) {
          baseTotalRef.current = totalPaymentDue
        }
        const baseTotal = baseTotalRef.current ?? totalPaymentDue
        const discountedTotal = values.discountedTotal ?? totalPaymentDue
        const discountAmount =
          baseTotal !== null && discountedTotal !== null
            ? baseTotal - discountedTotal
            : 0
        const isCalculationReady =
          totalPaymentDue !== null &&
          (!values.promoApplied || values.discountedTotal !== null)
        return (
          <ScrollErrorForm>
            <div className="form-section">
              <ParticipantsSummaryTable
                data={participantsSummaryData}
                title={<h3>Submission summary</h3>}
                subText={`Keep in mind, taxes will be deducted
              from the participant distribution 
              balance before final processing.`}
              />
              {isLoading && (
                <div className="complete-services-agreement-view">
                  <Spinner />
                </div>
              )}
              {isCreditCardPayment && isPromoCodeEnabled() && (
                <PromoCode
                  values={values}
                  setFieldValue={setFieldValue}
                  totalDiscount={discountAmount}
                  flashSuccessMessageHandler={flashSuccessMessageHandler}
                  setFieldError={setFieldError}
                  applyPromoCode={applyPromoCode}
                  orderParams={{ participants: participantsForSubmission }}
                  normalizeResponse={(response) => ({
                    isPromoCodeValid: response.isPromoCodeValid,
                    totalCost: response.summary.totalInspiraFees,
                  })}
                />
              )}
              {isCreditCardPayment && (
                <div className="payment-details-section">
                  <h2>Total payment due: {formatCurrency(discountedTotal)}</h2>
                  {((!showReplacePayment &&
                    discountedTotal > 0 &&
                    !hasPaymentMethod) ||
                    (!hasCardOnFile && discountedTotal > 0)) && (
                    <div className="payment-replace-card">
                      <p>You do not have a card on file with Inspira.</p>
                      <button
                        className="button-text"
                        onClick={(e) => {
                          e.preventDefault()
                          setShowAddPaymentModal(true)
                        }}
                      >
                        Add new card
                      </button>
                    </div>
                  )}
                  {touched.paymentDetails &&
                    errors.paymentDetails &&
                    discountedTotal > 0 && (
                      <Notification type={NotificationVariant.ERROR}>
                        {errors.paymentDetails.cardData}
                      </Notification>
                    )}
                  {showRemovePaymentModal && (
                    <ConfirmModal
                      onClose={() => setShowRemovePaymentModal(false)}
                      onConfirm={() => {
                        setShowRemovePaymentModal(false)
                        setIsNewPayment(true)
                        onConfirmDeletePayment()
                      }}
                      confirmContent="Yes, Replace"
                      className="remove-payment-modal"
                    >
                      <h2>Replace payment details</h2>
                      <p>
                        Keep in mind you can only have one card on file.
                        Replacing it here will replace it across the platform.
                      </p>
                    </ConfirmModal>
                  )}
                  {showAddPaymentModal && discountedTotal > 0 && (
                    <ConfirmModal
                      onClose={() => setShowAddPaymentModal(false)}
                      onConfirm={() => {
                        setShowAddPaymentModal(false)
                        setIsNewPayment(true)
                        onConfirmDeletePayment()
                      }}
                      confirmContent="Yes"
                      className="remove-payment-modal"
                    >
                      <h2>Add payment details</h2>
                    </ConfirmModal>
                  )}
                  {!showReplacePayment &&
                    hasPaymentMethod &&
                    hasCardOnFile &&
                    discountedTotal > 0 && (
                      <>
                        <div className="payment-replace-card">
                          <p>We'll charge your card on file with Inspira.</p>
                          <button
                            className="button-text"
                            onClick={(e) => {
                              e.preventDefault()
                              setShowRemovePaymentModal(true)
                            }}
                          >
                            Replace card
                          </button>
                        </div>
                        <div className="payment-details">
                          <div className="payment-details-header">
                            Card number
                          </div>
                          <div className="payment-details-text">
                            {paymentMethod.cardType}: {paymentMethod.cardLabel}
                          </div>
                          <div className="payment-details-header">
                            Billing address
                          </div>
                          <div className="payment-details-text">
                            {displayAttribute(paymentMethod.address1)},{' '}
                            {displayAttribute(paymentMethod.city)},{' '}
                            {displayAttribute(paymentMethod.state)}{' '}
                            {displayAttribute(paymentMethod.zipCode)}
                          </div>
                        </div>
                      </>
                    )}
                  {showReplacePayment && discountedTotal > 0 && (
                    <section className="new-payment-form">
                      <div id="add-new-payment-form-bd">
                        <PaymentFields sectionName="paymentDetails" />
                      </div>
                    </section>
                  )}
                  {discountedTotal > 0 && (
                    <ConfirmationCheckbox
                      name="confirmPayment"
                      label={`
                    ${PAYMENT_CONFIRMATION_TEXT} 
                    I also confirm that all records being submitted are employees or relatives of employees of ${currentUser.accountName}`}
                      errorText="You must agree to the disclosure to continue."
                    />
                  )}
                </div>
              )}
            </div>
            <FlowActions
              isSubmitting={isSubmitting}
              isSubmitDisabled={
                isCreditCardPayment ? !isCalculationReady : false
              }
              submitContent="Submit"
            />
          </ScrollErrorForm>
        )
      }}
    </Formik>
  )
}

ReviewAndSubmitForm.propTypes = propTypes
ReviewAndSubmitForm.defaultProps = defaultProps

export default ReviewAndSubmitForm
